/** GSM-7 single-segment SMS length (standard 160-character SMS). */
export const TAX_SMS_CART_LINK_MAX_LENGTH = 160;

/** Fallback cart-link host when payment config `baseUrlForSMS` is unset. */
export const DEFAULT_BASE_URL_FOR_SMS = 'https://oktaxrolls.com/';

/**
 * Resolves SMS cart-link base URL and county segment for oktaxrolls deep links.
 * Uses payment config first; if `baseUrlForSMS` or `countyNameForSMS` is empty, falls back to
 * {@link DEFAULT_BASE_URL_FOR_SMS} and global settings `countyName` respectively.
 * @param {object} [paymentSettingItem] SystemConfig payment slice
 * @param {object} [globalSettingItem] SystemConfig global slice (e.g. countyName)
 * @returns {{ baseUrlForSMS: string, countyNameForSMS: string|undefined }}
 */
export function resolveTaxSmsUrlParts(paymentSettingItem, globalSettingItem) {
  const paymentCfg = paymentSettingItem || {};
  const { countyName: globalCountyName } = globalSettingItem || {};
  let { baseUrlForSMS, countyNameForSMS } = paymentCfg;
  if (baseUrlForSMS == null || String(baseUrlForSMS).trim() === '') {
    baseUrlForSMS = DEFAULT_BASE_URL_FOR_SMS;
  }
  if (countyNameForSMS == null || String(countyNameForSMS).trim() === '') {
    countyNameForSMS = globalCountyName;
  }
  return { baseUrlForSMS, countyNameForSMS };
}

function cartLinkUrlForItems(base, countyNameForSMS, itemRows) {
  const itemsParam = (itemRows || [])
    .map(({ taxYear, taxId }) => `${taxYear}:${taxId}`)
    .join(',');
  return `${base}/cart-link/${countyNameForSMS}?items=${itemsParam}`;
}

/**
 * Cart deep-link for tax SMS (oktaxrolls `cart-link` + `items` query).
 * The returned URL length is capped at {@link TAX_SMS_CART_LINK_MAX_LENGTH} so it fits one
 * standard GSM-7 SMS segment when used as the message body. Items are included in order; if the
 * URL would exceed the limit, trailing items are omitted.
 * @param {object} params
 * @param {string} params.baseUrlForSMS - e.g. https://oktaxrolls.com
 * @param {string} params.countyNameForSMS - e.g. Cherokee
 * @param {Array<{ taxYear: string|number, taxId: string|number }>} params.items
 * @returns {string}
 */
export default function buildTaxSmsCartLinkUrl({ baseUrlForSMS, countyNameForSMS, items }) {
  const base = String(baseUrlForSMS || '').replace(/\/$/, '');
  const maxLen = TAX_SMS_CART_LINK_MAX_LENGTH;
  const rows = Array.isArray(items) ? items.slice() : [];
  let useRows = rows;
  let url = cartLinkUrlForItems(base, countyNameForSMS, useRows);
  while (url.length > maxLen && useRows.length > 0) {
    useRows = useRows.slice(0, useRows.length - 1);
    url = cartLinkUrlForItems(base, countyNameForSMS, useRows);
  }
  return url;
}
